import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../web/validation";
import {clone} from "lodash";
import {Entity} from "./entity";
import {
  SiteAssignmentCompanyAssocArrayValidator,
  SiteAssignmentCompanyAssocEntity,
  SiteAssignmentSiteAssocArrayValidator,
  SiteAssignmentSiteAssocEntity
} from "./site-assignment-assoc";
import {DatabaseCode} from "./database-code";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {toTransportCategory, TransportCategory} from "./transport-category";

/** Validator for a site assignment type. */
export const SiteAssignmentValidator = t.intersection([
  t.type({
    id: t.string,
    siteId: t.string,
    regionId: t.string,
    type: t.string,
    strategy: t.string,
    category: t.string
  }),
  t.partial({
    siteName: t.string,
    companyAssocs: SiteAssignmentCompanyAssocArrayValidator,
    siteAssocs: SiteAssignmentSiteAssocArrayValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number
  })
]);

/** Validator for an array of site assignments. */
export const SiteAssignmentArrayValidator = t.array(SiteAssignmentValidator);

export type SiteAssignment = t.TypeOf<typeof SiteAssignmentValidator>;
export type SiteAssignmentArray = t.TypeOf<typeof SiteAssignmentArrayValidator>;

export type SiteAssignmentStrategy = DatabaseCode;

const ranked: SiteAssignmentStrategy = {
  code: "RANKED",
  displayValue: {
    id: "ots.ui.entity.siteassignment.strategy.ranked",
    defaultMessage: "Ranked"
  }
};

const roundRobin: SiteAssignmentStrategy = {
  code: "ROUND_ROBIN",
  displayValue: {
    id: "ots.ui.entity.siteassignment.strategy.roundrobin",
    defaultMessage: "Round Robin"
  }
};

const rulesBased: SiteAssignmentStrategy = {
  code: "RULES_BASED",
  displayValue: {
    id: "ots.ui.entity.siteassignment.strategy.rulesbased",
    defaultMessage: "Rules Based"
  }
};

export const siteAssignmentStrategies = {
  ranked,
  roundRobin,
  rulesBased
};

export const toSiteAssignmentStrategy = (
  code: string
): SiteAssignmentStrategy => {
  for (const type of Object.values(siteAssignmentStrategies)) {
    if (type.code === code) {
      return type;
    }
  }
  return {code, displayValue: {id: code, defaultMessage: code}};
};

export type SiteAssignmentType = DatabaseCode;

const company: SiteAssignmentType = {
  code: "COMPANY",
  displayValue: {
    id: "ots.ui.entity.siteassignment.type.company",
    defaultMessage: "Company"
  }
};

const pickupSite: SiteAssignmentType = {
  code: "PICKUP_SITE",
  displayValue: {
    id: "ots.ui.entity.siteassignment.type.pickupsite",
    defaultMessage: "Pickup Site"
  }
};

const deliverySite: SiteAssignmentType = {
  code: "DELIVERY_SITE",
  displayValue: {
    id: "ots.ui.entity.siteassignment.type.deliverysite",
    defaultMessage: "Delivery Site"
  }
};

export const siteAssignmentTypes = {
  company,
  pickupSite,
  deliverySite
};

export const toSiteAssignmentType = (code: string): SiteAssignmentType => {
  for (const type of Object.values(siteAssignmentTypes)) {
    if (type.code === code) {
      return type;
    }
  }
  return {code, displayValue: {id: code, defaultMessage: code}};
};

/**
 * A site assignment.
 */
@Exclude()
export class SiteAssignmentEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  siteId!: string;

  @Expose()
  siteName?: string

  @Expose()
  regionId!: string;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toSiteAssignmentType))
  type!: SiteAssignmentType;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toSiteAssignmentStrategy))
  strategy!: SiteAssignmentStrategy;

  @Expose()
  @Transform(getDatabaseCodeTransformer(toTransportCategory))
  category!: TransportCategory;

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Type(() => SiteAssignmentCompanyAssocEntity)
  @Expose()
  companyAssocs: SiteAssignmentCompanyAssocEntity[] | undefined;

  @Type(() => SiteAssignmentSiteAssocEntity)
  @Expose()
  siteAssocs: SiteAssignmentSiteAssocEntity[] | undefined;

  readonly entityType = "SITEASSIGN";

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for SiteAssignmentEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: SiteAssignment): SiteAssignmentEntity {
    return plainToInstance(SiteAssignmentEntity, value, {
      excludeExtraneousValues: true
    });
  }

  clone(): SiteAssignmentEntity {
    return clone(this);
  }

  loadTypeValue(): Promise<SiteAssignment> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(SiteAssignmentValidator, object);
  }

  asTypeValue(): SiteAssignment {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    const value = validateJSONNow(SiteAssignmentValidator, object);
    value.companyAssocs = this.companyAssocs?.map((a) => a.toTypeValueNow());
    value.siteAssocs = this.siteAssocs?.map((a) => a.toTypeValueNow());
    return value
  }
}

export interface SiteAssignmentTemplate {
  regionId: string
  objectIds: string[]
  objectType: string
  template: Partial<SiteAssignment>
}
